import { useTranslations } from '@vocab/react';
import translations from './.vocab';
import React, { useCallback, useState } from 'react';
import {
  Actions,
  Alert,
  Box,
  Button,
  Heading,
  Stack,
  Text,
} from 'braid-design-system';

import { PriceLookUpInputs } from '../PriceLookUpInputs/PriceLookUpInputs';
import {
  type PriceAttribute,
  type PriceAttributes,
  AdPriceSummary,
} from '../AdPriceSummary/AdPriceSummary';
import type { JobTitleChangeEvent } from '../JobTitle/JobTitle';
import { AdvertiserNation } from '../../utils/AdvertiserNation';
import { TrackingContext } from '../../context/trackingContext';
import { usePricingContext } from 'src/App/context/PricingContextProvider';
import type { LocationSuggestion } from '@seek/job-location';
import type {
  JobCategoryChangeEvent,
  JobCategoryChangeHandler,
} from '@seek/job-category';
import type { Category } from 'src/App/generated/graphql-types';
import { Helmet } from 'react-helmet';

interface PageState {
  roleTitle?: string;
  categoryDetails?: Category;
  location?: PriceAttribute;
  showPrices: boolean;
}

interface PageProps {
  advertiserId?: string;
  advertiserNation: AdvertiserNation;
}

const initialState: PageState = {
  roleTitle: undefined,
  categoryDetails: undefined,
  location: undefined,
  showPrices: false,
};

const priceAttributesInitial: PriceAttributes = {
  roleTitle: undefined,
  subClassification: undefined,
  location: undefined,
};

export const HirerAdPriceLookup = (props: PageProps) => {
  const { t } = useTranslations(translations);
  const { isPreview } = usePricingContext();
  const [state, setState] = useState<PageState>(initialState);
  const [priceAttributes, setPriceAttributes] = useState<PriceAttributes>(
    priceAttributesInitial,
  );
  const tracking = React.useContext(TrackingContext);

  const onJobTitleChange = (jobTitleEvent: JobTitleChangeEvent) => {
    const { isValid, value } = jobTitleEvent;
    setState({ ...state, roleTitle: isValid ? value : undefined });
  };

  const onCategoryChange: JobCategoryChangeHandler = ({
    category,
  }: JobCategoryChangeEvent) => {
    tracking.onClassificationSelected();
    setState({
      ...state,
      categoryDetails:
        category && category?.categoryId > 0 ? category : undefined,
    });
  };

  const getLocationId = (locationValue: string): number | undefined => {
    const locationId: number = parseInt(locationValue);
    return isNaN(locationId) ? undefined : locationId;
  };

  const onLocationChange = useCallback(
    (jobLocationEvent: LocationSuggestion) => {
      const { isValid, location } = jobLocationEvent;

      tracking.onLocationSelected();
      setState({
        ...state,
        location: isValid
          ? {
              text: location.text,
              id: location.value ? getLocationId(location.value) : undefined,
            }
          : undefined,
      });
    },
    [JSON.stringify(state)],
  );

  const updatePriceAttributes = () => {
    const subClassification = state.categoryDetails?.subCategoryId?.toString();
    const updatedPriceAttributes: PriceAttributes = {
      location: state.location,
      subClassification,
      roleTitle: state.roleTitle,
    };
    setPriceAttributes(updatedPriceAttributes);
  };

  const allowFetchPrices = () => {
    setState({
      ...state,
      showPrices: true,
    });
  };

  const shouldFetchPrice = () =>
    !!priceAttributes &&
    !!priceAttributes.roleTitle &&
    !!priceAttributes.subClassification &&
    priceAttributes.subClassification !== '-1' &&
    !!priceAttributes.location &&
    showPrices;

  const onCheckPrices = () => {
    updatePriceAttributes();
    allowFetchPrices();
    if (!!state && state.location && state.categoryDetails) {
      tracking.onPriceQuery(
        state.location.id!,
        state.categoryDetails.categoryName,
        state.categoryDetails.subCategoryName,
      );
    }
  };

  const { showPrices } = state;
  const { advertiserId, advertiserNation } = props;

  return (
    <Stack space={'large'}>
      <Helmet
        title={
          isPreview
            ? 'SEEK employer - Ad price lookup preview'
            : 'SEEK employer - Ad price lookup'
        }
      />
      <Heading level="2">
        {isPreview
          ? t('Preview variable ad prices')
          : t('Check the price for your ad')}
      </Heading>
      <Box>
        {isPreview && (
          <Box paddingBottom={'small'}>
            <Alert tone="info">
              <Text>{t('Use this tool to see a preview...')}</Text>
            </Alert>
          </Box>
        )}
        <Stack space={'medium'}>
          <PriceLookUpInputs
            onJobTitleChange={onJobTitleChange}
            onLocationChange={onLocationChange}
            onCategoryChange={onCategoryChange}
            advertiserId={advertiserId}
          />
          <Actions>
            <Button tone="formAccent" onClick={onCheckPrices}>
              {t('Check price')}
            </Button>
          </Actions>
        </Stack>
        {shouldFetchPrice() && (
          <AdPriceSummary
            advertiserId={advertiserId}
            priceAttributes={priceAttributes}
            advertiserNation={advertiserNation}
          />
        )}
      </Box>
    </Stack>
  );
};
